import ProtectedComponent from './protected-component.js';
import { formatEventDate, formatEventTime, parseEventDate } from '../utils/date-helpers.js';
import { escapeHtml } from '../utils/html-utils.js';

const TWO_WEEKS_MS = 14 * 24 * 60 * 60 * 1000;

class UpcomingEventsBanner extends ProtectedComponent {
  constructor() {
    super();
    this.events = [];
    this.hasMore = false;
    this.isLoading = false;
    this.error = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this.render();
  }

  async load() {
    if (!this.isAuthenticated()) {
      this.clear();
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.render();

    try {
      const now = new Date();
      const endDate = new Date(now.getTime() + TWO_WEEKS_MS);
      const params = new URLSearchParams({
        upcoming: 'true',
        startDate: now.toISOString(),
        endDate: endDate.toISOString()
      });

      const response = await window.API.request(`/events?${params.toString()}`);
      const upcoming = Array.isArray(response?.upcoming_events) ? response.upcoming_events : [];

      const filtered = upcoming
        .map(event => ({
          ...event,
          _parsedStart: parseEventDate(event.start_time, event.timezone || 'UTC')
        }))
        .filter(event => {
          const { _parsedStart } = event;
          if (!_parsedStart || Number.isNaN(_parsedStart.getTime())) {
            return false;
          }
          return _parsedStart >= now && _parsedStart <= endDate;
        })
        .sort((a, b) => a._parsedStart - b._parsedStart);

      this.hasMore = filtered.length > 2;
      this.events = filtered.slice(0, 2).map(event => ({
        ...event,
        startDateLabel: formatEventDate(event._parsedStart),
        startTimeLabel: formatEventTime(event._parsedStart)
      }));
    } catch (error) {
      this.error = error?.message || 'Unable to load upcoming events.';
      window.logger.error('UpcomingEventsBanner: failed to load events', error);
    } finally {
      this.isLoading = false;
      this.render();
    }
  }

  clear() {
    this.events = [];
    this.hasMore = false;
    this.isLoading = false;
    this.error = null;
    this.innerHTML = '';
  }

  render() {
    if (this.isLoading) {
      this.innerHTML = `
        <section class="upcoming-events-banner loading">
          <div class="banner-header">
            <div class="banner-title">
              <span class="banner-icon" aria-hidden="true">🎈</span>
              <span>Upcoming Fun</span>
            </div>
          </div>
          <div class="banner-body">
            <div class="loading-state">
              <div class="spinner" role="status" aria-live="polite"></div>
              <p>Checking the party calendar…</p>
            </div>
          </div>
        </section>
      `;
      return;
    }

    if (this.error) {
      this.innerHTML = `
        <section class="upcoming-events-banner error">
          <div class="banner-header">
            <div class="banner-title">
              <span class="banner-icon" aria-hidden="true">🎈</span>
              <span>Upcoming Fun</span>
            </div>
          </div>
          <div class="banner-body">
            <div class="error-state">
              <span class="error-icon" aria-hidden="true">😿</span>
              <p class="error-message">${escapeHtml(this.error)}</p>
            </div>
          </div>
        </section>
      `;
      return;
    }

    if (!this.events || this.events.length === 0) {
      this.innerHTML = '';
      return;
    }

    const eventsHtml = this.events.map(event => `
      <article class="banner-event-card">
        <a href="/events/${event.id}" class="event-link" data-event-id="${event.id}">
          <div class="event-date">
            <span class="event-weekday">${event.startDateLabel}</span>
            <span class="event-time">${event.startTimeLabel}</span>
          </div>
          <div class="event-details">
            <h3 class="event-title">${escapeHtml(event.title)}</h3>
            ${event.location ? `<p class="event-location">${escapeHtml(event.location)}</p>` : ''}
          </div>
          <span class="event-arrow" aria-hidden="true">→</span>
        </a>
      </article>
    `).join('');

    this.innerHTML = `
      <section class="upcoming-events-banner">
        <div class="banner-header">
          <div class="banner-title">
            <span class="banner-icon" aria-hidden="true">🎈</span>
            <span>Don't miss these!</span>
          </div>
          <span class="banner-subtitle">Happening within the next two weeks</span>
        </div>
        <div class="banner-body">
          ${eventsHtml}
        </div>
        ${this.hasMore ? `
          <div class="banner-footer">
            <button type="button" class="more-events-btn">
              <span>See more events</span>
              <span class="sparkle" aria-hidden="true">✨</span>
            </button>
          </div>
        ` : ''}
      </section>
    `;

    this.attachEventHandlers();
  }

  attachEventHandlers() {
    this.querySelectorAll('.event-link').forEach(link => {
      link.addEventListener('click', (event) => {
        event.preventDefault();
        const targetId = event.currentTarget.getAttribute('data-event-id');
        if (targetId) {
          window.router.navigate(`/events/${targetId}`);
        }
      });
    });

    const moreBtn = this.querySelector('.more-events-btn');
    if (moreBtn) {
      moreBtn.addEventListener('click', () => {
        window.router.navigate('/events/calendar');
      });
    }
  }

}

customElements.define('upcoming-events-banner', UpcomingEventsBanner);

export default UpcomingEventsBanner;

